/**
 * Le test jouable de bout en bout SANS jeton — l'intro, la simulation, puis ce
 * que la fiche de Kayla recevrait.
 *
 * Servi à deux endroits, et c'est voulu : le harnais dev (`vente-dev.html`)
 * et la route `/regie-test-vente` du cockpit, derrière le même gate WorkOS que
 * l'admin du test (`VenteApercuGate`) — PAS publique : la consigne et le
 * scénario sont la réponse au test. Kayla doit pouvoir le JOUER avant qu'un
 * candidat le passe : un écran d'épreuve se juge en le faisant.
 *
 * ⛔ Rien n'est envoyé : aucun jeton, aucun POST. À la fin, l'écran AFFICHE le
 * résultat — et le payload exact que la page candidat posterait
 * (`versPayload`), pour qu'on voie ce que la fiche recevra.
 */
import { useState } from "react";

import { Button, Carte, Ligne, Lignes, Section, Temps } from "@/shared/ui";
import { Check, X } from "@/shared/ui/icons";

import { CadreDuPoste } from "../PhoneFrame";
import { IntroDuTest } from "./IntroDuTest";
import { VenteScreen } from "./VenteScreen";
import { versPayload, type MesureDeLIntro, type Resultat } from "./notation";
import { LIBELLES_ETAPES } from "./scenario";

type Ecran = { quoi: "intro" } | { quoi: "simulation"; intro: MesureDeLIntro } | { quoi: "fin"; resultat: Resultat };

export function ApercuVente() {
  const [ecran, setEcran] = useState<Ecran>({ quoi: "intro" });
  const [cle, setCle] = useState(0);

  return (
    <CadreDuPoste subtitle="Le test (aperçu)" stepIndex={2}>
      {ecran.quoi === "intro" && (
        <IntroDuTest key={cle} onFini={(intro) => setEcran({ quoi: "simulation", intro })} />
      )}
      {ecran.quoi === "simulation" && (
        <VenteScreen key={cle} intro={ecran.intro} onFini={(resultat) => setEcran({ quoi: "fin", resultat })} />
      )}
      {ecran.quoi === "fin" && (
        <FinDePartie
          resultat={ecran.resultat}
          onRejouer={() => {
            setCle((k) => k + 1);
            setEcran({ quoi: "intro" });
          }}
        />
      )}
    </CadreDuPoste>
  );
}

/** Un nombre et son unité (« 1 s », « 15 min », « 40 € ») ne se coupent pas en fin de ligne : espace insécable. */
function sansCoupure(note: string): string {
  return note.replace(/(\d) (s|min|€|EUR|USD)(?![\p{L}])/gu, `$1${String.fromCharCode(160)}$2`);
}

function FinDePartie({ resultat, onRejouer }: { resultat: Resultat; onRejouer: () => void }) {
  const [voirLePayload, setVoirLePayload] = useState(false);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-[760px] flex-col gap-4 pb-8">
        <div>
          <h2 className="text-[19px] font-bold text-fg">Ce que la fiche recevra</h2>
          <p className="text-[14px] text-muted">
            {resultat.reussis}/{resultat.total} sur la mécanique ·{" "}
            {resultat.dureeSecondes === null ? "—" : <Temps.Duree ms={resultat.dureeSecondes * 1000} secondes />} de
            simulation
            {resultat.intro ? (
              <>
                {" "}
                · <Temps.Duree ms={resultat.intro.ms} secondes /> sur l'intro
              </>
            ) : null}
          </p>
          <p className="mt-1 text-[13px] text-faint">Aperçu : rien n'a été envoyé.</p>
        </div>

        <Carte>
          <ul className="flex flex-col gap-2.5">
            {resultat.criteres.map((c) => (
              <li key={c.cle} className="flex items-start gap-2.5 text-[14px] leading-snug">
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center ${c.reussi === true ? "text-valide" : c.reussi === false ? "text-erreur" : "text-faint"}`}
                  aria-hidden
                >
                  {c.reussi === true ? <Check className="h-4 w-4" /> : c.reussi === false ? <X className="h-4 w-4" /> : "·"}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={c.reussi === null ? "text-faint" : "text-fg"}>{c.libelle}</span>
                  {c.note ? <span className="text-muted"> — {sansCoupure(c.note)}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </Carte>

        <Section titre="Le chrono, étape par étape" remplissage="aucun">
          <Lignes ariaLabel="Le chrono, étape par étape">
            {resultat.marques.map((m) => (
              <Ligne
                key={m.etape}
                etiquette={<span className="block whitespace-normal leading-snug">{LIBELLES_ETAPES[m.etape]}</span>}
                valeur={m.fin === null ? "—" : Temps.formatChrono(m.fin - m.debut)}
              />
            ))}
          </Lignes>
        </Section>

        <Section titre="Ce qu'il a écrit — à toi de lire">
          {resultat.sesMessages.length === 0 ? (
            <p className="text-[13px] text-faint">Il n'a rien écrit.</p>
          ) : (
            <ol className="flex flex-col gap-2">
              {resultat.sesMessages.map((m, i) => (
                <li key={i} className="text-[14px] leading-snug text-fg">
                  <span className="text-faint">{i + 1}.</span> {m}
                </li>
              ))}
            </ol>
          )}
        </Section>

        <div className="flex flex-col items-start gap-2">
          <Button variant="secondary" size="lg" onClick={() => setVoirLePayload((v) => !v)}>
            {voirLePayload ? "Masquer le résultat brut" : "Voir le résultat brut (ce que la page posterait)"}
          </Button>
          {voirLePayload && (
            <Carte remplissage="sm">
              <pre className="max-h-96 overflow-auto text-[12px] leading-snug text-muted">
                {JSON.stringify(versPayload(resultat), null, 2)}
              </pre>
            </Carte>
          )}
        </div>

        <Button variant="secondary" size="lg" className="self-start" onClick={onRejouer}>
          Rejouer depuis le début
        </Button>
      </div>
    </div>
  );
}
